import { AppSidebar } from '@/components/app-sidebar';
import { BillingRequiredDialog } from '@/components/billing/BillingRequiredDialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Separator } from '@/components/ui/separator';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import {
  PageMetadataProvider,
  usePageMetadata,
} from '@/contexts/PageMetadataContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';
import { useCurrentPlan } from '@/hooks/useCurrentPlan'; // + Import the hook
import { Home } from 'lucide-react';
import React from 'react';
import { Link, Outlet, useMatches } from '@tanstack/react-router';

const AppBreadcrumb = () => {
  const matches = useMatches();

  if (matches.length === 1 || matches[matches.length - 1].pathname === '/') {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage className="flex items-center hover:text-purple-500 transition-colors">
              <Home className="h-4 w-4" />
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Build crumbs manually based on the current path
  const currentMatch = matches[matches.length - 1];
  const crumbs = [];

  // If we're in endpoints or deeper, add the Endpoints crumb
  if (currentMatch.pathname.includes('endpoints')) {
    crumbs.push({
      title: 'Endpoints',
      path: '/endpoints',
    });

    // If we're on a specific endpoint, add its crumb
    if (currentMatch.params && '$id' in currentMatch.params) {
      const id = currentMatch.params.$id as string;
      crumbs.push({
        title: `Endpoint ${id.split('-')[0]}`,
        path: currentMatch.pathname,
      });
    }
  }


  if (currentMatch.pathname === '/connection-builder') {
    crumbs.push({
      title: 'Connection Builder',
      path: '/connection-builder',
    });
  }

  if (currentMatch.pathname === '/connections') {
    crumbs.push({
      title: 'Connections',
      path: '/connections',
    });
  }

  if (currentMatch.pathname === '/issues') {
    crumbs.push({
      title: 'Issues',
      path: '/issues',
    });
  }

  if (currentMatch.pathname === '/circuit-breakers') {
    crumbs.push({
      title: 'Circuit breakers',
      path: '/circuit-breakers',
    });
  }

  if (currentMatch.pathname === '/device-codes') {
    crumbs.push({
      title: 'Device Codes',
      path: '/device-codes',
    });
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/" className="hover:text-purple-500 transition-colors">
              <Home className="h-4 w-4" />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {crumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path}>
            <BreadcrumbSeparator className="text-muted-foreground/50" />
            <BreadcrumbItem>
              {index === crumbs.length - 1 ? (
                <BreadcrumbPage className="text-purple-300/90 font-semibold">
                  {crumb.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link
                    to={crumb.path}
                    className="hover:text-purple-500 transition-colors"
                  >
                    {crumb.title}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const TopBar = () => {
  const { description, actions } = usePageMetadata();

  return (
    <header className="flex h-14 items-center gap-4 border-b border-white/10 bg-background/50 backdrop-blur-xs px-6">
      <SidebarTrigger className="hover:bg-white/5 hover:text-purple-500 transition-colors" />
      <Separator orientation="vertical" className="bg-white/10" />
      <AppBreadcrumb />
      {description && (
        <p className="text-sm text-muted-foreground ml-2">{description}</p>
      )}
      <div className="flex-1" />
      {actions && <div className="flex gap-3 mr-4">{actions}</div>}
    </header>
  );
};

const Layout = () => {
  useCurrentPlan();

  return (
    <PageMetadataProvider>
      <WebSocketProvider>
        <SidebarProvider>
          <div className="flex bg-linear-to-b from-background to-background/95 w-full">
            <AppSidebar />
            <div className="flex-1 flex flex-col">
              <TopBar />
              <div className="flex-1 w-full p-6">
                <Outlet />
              </div>
            </div>
          </div>
        </SidebarProvider>
        <BillingRequiredDialog />
      </WebSocketProvider>
    </PageMetadataProvider>
  );
};

export default Layout;
